/**
 * Order of the deliverable cards on the program pages (ProgramDetail /
 * CustomProgram) and the portal plan grids.
 *
 * Source of truth is the CRM-editable `settings/deliverableOrder` doc — an
 * array of alaCarteItems ids, edited from the à la carte template page. The
 * name-regex list below is the fallback for anything not in that array (new
 * catalog items, or before an order has ever been saved).
 */

/** Firestore: `settings/{DELIVERABLE_ORDER_DOC_ID}` → `{ order: string[] }` */
export const DELIVERABLE_ORDER_DOC_ID = 'deliverableOrder';

/**
 * Fallback priority for deliverables not covered by the saved order (items not
 * in this list go last). On-site visit is excluded upstream, not here.
 */
export const DELIVERABLE_ORDER = [
  { match: /portal\s*access/i, order: 1 },
  { match: /1:1\s*strat|1:1\s*strategy\s*call|strat\s*call|implementation\s*hours?/i, order: 2 },
  { match: /hiring\s*service|hiring/i, order: 3 },
  { match: /bba\s*summit|summit\s*ticket/i, order: 4 },
  { match: /cqc\s*visit/i, order: 5 },
  { match: /google\s*my\s*business|gmb|google\s*business\s*profile/i, order: 6 },
  { match: /ai\s*receptionist|receptionist/i, order: 7 },
  { match: /automations?/i, order: 8 },
  { match: /website\s*chatbot|chatbot/i, order: 9 },
  { match: /templated\s*website|template.*website|website.*template|templated.*seo|website\s*buildout|buildout/i, order: 11 },
  { match: /fully\s*custom\s*website|custom\s*website|custom\s*website.*seo|website.*custom.*seo/i, order: 12 },
  { match: /seo\s*management/i, order: 10 },
  { match: /newsletter/i, order: 13 },
  { match: /social\s*media/i, order: 14 },
  { match: /paid\s*ads/i, order: 15 },
];

export function getDeliverableOrder(itemName) {
  const name = (itemName || '').toLowerCase();
  for (const { match, order } of DELIVERABLE_ORDER) {
    if (match.test(name)) return order;
  }
  return 999;
}

/** Normalize the stored `order` field to a clean array of ids. */
export function normalizeDeliverableOrderIds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id) => typeof id === 'string' && id.trim() !== '');
}

/**
 * Pull the saved deliverable order out of a `getDocs(collection(db,'settings'))`
 * snapshot — the program pages already load that collection for `itemOrder`.
 * @returns {string[]} item ids in display order (empty when never saved)
 */
export function readDeliverableOrderFromSettingsDocs(docs) {
  const found = (docs || []).find((d) => d.id === DELIVERABLE_ORDER_DOC_ID);
  if (!found) return [];
  return normalizeDeliverableOrderIds(found.data()?.order);
}

/**
 * Sort catalog items for display. Items listed in `savedOrderIds` come first in
 * exactly that order; everything else follows, ranked by the name-regex
 * fallback and then alphabetically.
 *
 * @param {Array<{id?: string, name?: string}>} items
 * @param {string[]} [savedOrderIds] ids from `settings/deliverableOrder`
 */
export function sortCatalogByDeliverableOrder(items, savedOrderIds) {
  const saved = normalizeDeliverableOrderIds(savedOrderIds);
  const rank = new Map(saved.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const savedA = rank.has(a.id) ? rank.get(a.id) : Infinity;
    const savedB = rank.has(b.id) ? rank.get(b.id) : Infinity;
    if (savedA !== savedB) return savedA - savedB;
    const orderA = getDeliverableOrder(a.name);
    const orderB = getDeliverableOrder(b.name);
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '');
  });
}
